import Button from './ui/Button';
import { Heart, ExternalLink, MessageCircle, LogOut, Users, Award, CheckCircle, Settings } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useAdmin } from '../hooks/useAdmin';
import { adminViewService } from '../services/maintenanceService';

export default function CommunityTestingEndedPage({ feedbackUrl }) {
  const { signOut } = useAuth();
  const { isAdmin } = useAdmin();

  const handleFeedbackClick = () => {
    const newWindow = window.open(feedbackUrl, '_blank', 'noopener,noreferrer');
    if (!newWindow) {
      // Fallback for popup blockers
      window.location.href = feedbackUrl;
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleReturnToAdmin = () => {
    adminViewService.setViewMode('admin');
    window.location.href = '/admin';
  };

  return (
    <div className="min-h-screen">
      {/* Skip to main content link for keyboard users */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-primary text-white px-4 py-2 rounded-lg z-50 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-primary"
      >
        Skip to main content
      </a>

      {/* Branded Navbar */}
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <div className="text-2xl font-bold bg-gradient-hero bg-clip-text text-transparent" role="img" aria-label="CertifiAble logo">
                CertifiAble
              </div>
              {/* Fallback text for better contrast when gradient fails */}
              <span className="sr-only">CertifiAble</span>
            </div>

            {/* Logout button and admin escape */}
            <div className="flex items-center gap-3">
              {/* Admin escape button - only show if admin is in user view mode */}
              {isAdmin && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleReturnToAdmin}
                  aria-label="Return to admin dashboard"
                  className="focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                >
                  <Settings className="w-4 h-4 mr-2" aria-hidden="true" />
                  Return to Admin
                </Button>
              )}
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                aria-label="Sign out of CertifiAble account"
                className="focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              >
                <LogOut className="w-4 h-4 mr-2" aria-hidden="true" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary to-secondary py-24 overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0 opacity-10" aria-hidden="true">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-4xl mb-8" role="img" aria-label="Celebration">🎉</div>
          
          <h1 className="text-7xl font-bold text-white mb-6 drop-shadow-lg">
            CertifiAble
          </h1>
          
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-8 mb-12 max-w-4xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-4">
              <CheckCircle className="w-8 h-8 text-white" aria-hidden="true" />
              <h2 className="text-3xl font-bold text-white drop-shadow-md">
                Community Testing Complete
              </h2>
            </div>
            <p className="text-xl text-white drop-shadow-lg leading-relaxed font-medium">
              Thank you to all the ASL interpreters who participated in our community testing phase. 
              Your feedback and insights have been invaluable in shaping CertifiAble.
            </p>
          </div>
        </div>
      </section>

      {/* Thank You Section */}
      <section className="py-20 bg-white" id="main-content">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-6xl mb-8">
            <Heart className="w-24 h-24 text-red-500 mx-auto" role="img" aria-label="Heart symbol" />
          </div>
          
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Thank You, ASL Interpreter Community
          </h2>
          
          <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl p-8 mb-12">
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              We are deeply grateful to the ASL interpreter community for the opportunity to build 
              CertifiAble with your needs in mind. While our community testing phase has concluded, 
              your dedication to professional development continues to inspire us.
            </p>
            
            <div className="grid md:grid-cols-2 gap-8 mt-8">
              <div className="flex items-start gap-4">
                <Users className="w-8 h-8 text-primary flex-shrink-0 mt-1" aria-hidden="true" />
                <div className="text-left">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Community Driven</h3>
                  <p className="text-gray-600">
                    Built by an interpreter, for interpreters, with community feedback at every step.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <Award className="w-8 h-8 text-primary flex-shrink-0 mt-1" aria-hidden="true" />
                <div className="text-left">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Professional Excellence</h3>
                  <p className="text-gray-600">
                    Designed to support your CEU tracking and professional certification requirements.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* What's Next Section */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-8 mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              What's Next?
            </h3>
            <p className="text-lg text-gray-800 mb-6 leading-relaxed font-medium">
              We're currently evaluating the future of CertifiAble based on community feedback and engagement. 
              While we're not actively accepting new users at this time, we remain committed to the 
              ASL interpreter community.
            </p>
            
            <div className="bg-white/80 backdrop-blur-sm rounded-lg p-6 border border-gray-200">
              <p className="text-gray-800 leading-relaxed font-medium">
                If you have questions about your CEU tracking needs or would like to stay informed 
                about future developments, we'd love to hear from you.
              </p>
            </div>
          </div>

          {feedbackUrl && (
            <div className="bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20 rounded-xl p-8 mb-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                <MessageCircle className="w-8 h-8 inline-block mr-3 text-primary" aria-hidden="true" />
                Share Your Thoughts
              </h3>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Your feedback continues to be valuable to us. Share your thoughts about CEU tracking, 
                professional development tools, or your experience with CertifiAble.
              </p>
              
              <Button 
                variant="primary" 
                size="lg" 
                onClick={handleFeedbackClick}
                className="inline-flex items-center gap-3 min-h-[44px] focus:ring-2 focus:ring-primary focus:ring-offset-2"
                aria-label="Share your feedback - opens in new window"
              >
                <ExternalLink className="w-5 h-5" aria-hidden="true" />
                Share Your Feedback
              </Button>
            </div>
          )}

          <div className="text-center">
            <p className="text-lg text-gray-600 leading-relaxed mb-4">
              Thank you for being part of the CertifiAble journey.
            </p>
            <p className="text-primary font-semibold text-xl">
              — Danny Frank, Frankly Interpreting LLC
            </p>
            <p className="text-gray-500 text-sm mt-2">
              Built by an interpreter, for interpreters
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}